import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { Observable, Subscription, of, throwError, combineLatest } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { LatLng } from 'leaflet';

import { DropdownComponent, DropdownOptionsObject } from '@zonar-ui/filter';

import { ICategory, IGetCategoryParams, IZonePostModel } from '@src/app/models/zone.model';
import { CoreZoneApiService } from '@src/app/services/core-api/api/core-zone-api.service';
import { DialogComponent } from '@src/app/components/dialog/dialog.component';
import { ReloadMapService } from '@src/app/services/reload-map-service/reload-map.service';
import { SelectedCompanyService } from '@src/app/services/selected-company-service/selected-company.service';
import { SnackbarService } from '@src/app/services/snackbar/snackbar.service';
import { PAGE_URL } from '@src/app/constants/url.const';
import { MapService } from '@src/app/services/map-service/map.service';
import { LatLngArray } from '@src/app/services/map-service/map-types';
import { SNACKBAR_TEXT } from '@src/app/constants/messages.constants';
import { STATUSAPI } from '@src/app/constants/status-api.const';
import { VALIDATION_PATTERNS } from '@src/app/constants/validation.const';

const MIN_POLYGON_LINES = 3;

@Component({
  selector: 'app-create-zone',
  templateUrl: './create-zone.component.html',
  styleUrls: ['./create-zone.component.scss'],
})

export class CreateZoneComponent implements OnInit, OnDestroy {
  @ViewChild(DropdownComponent) dropdownCategories: DropdownComponent;

  zoneName: FormControl<string> = new FormControl('', [
    Validators.required,
    Validators.pattern(VALIDATION_PATTERNS.NO_ONLY_WHITESPACE)
  ]);
  selectedCategory: FormControl<ICategory | null> = new FormControl(null, Validators.required);

  companyId$: Observable<string> = this.companyService.getCurrentCompany();
  data$: Observable<DropdownOptionsObject[]>;

  isMultiple = false;
  enableAllOptions = false;
  blueCheckmarks = true;
  isSaveBtnDisabled = true;
  pageTitle: string;
  categories: ICategory[] = [];
  zoneForm: FormGroup;
  latLngs: LatLngArray = [];

  private subscription: Subscription = new Subscription();
  private subscriptionMapData: Subscription = new Subscription();

  constructor(
    private companyService: SelectedCompanyService,
    private coreZoneApiService: CoreZoneApiService,
    private router: Router,
    private dialog: MatDialog,
    private reloadMapService: ReloadMapService,
    private snackbarService: SnackbarService,
    private mapService: MapService
  ) {
    this.zoneForm = new FormGroup({
      zoneName: this.zoneName,
      selectedCategory: this.selectedCategory,
    });
  }

  ngOnInit(): void {
    this.setPageTitle();
    this.loadCategories();
    this.initializeCollectData();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getCategoryData(categories: ICategory[]): DropdownOptionsObject[] {
    return categories.map(category => ({
      title: category.name,
      value: {
        name: category.name,
        id: category.id,
        color: category.color,
      } as ICategory,
      matIcon: {
        name: 'circle',
        color: category.color
      }
    }));
  }

  handleFilterChange(values: any, paramName: string) {
    const data: any = {};
    data[paramName] = values;
    // TO-DO: Update model for values
    return;
  }

  onClose() {
    this.reloadMapService.reinitializeMap();
    this.router.navigate([PAGE_URL.ZONE_EDITOR_URL]);
  }

  onSave() {
    if (!this.isSaveBtnDisabled) {
      this.saveZone();
    }
  }

  private initializeCollectData(): void {
    this.subscriptionMapData = this.getMapPolygonData().subscribe();
    this.subscription.add(this.subscriptionMapData);
    this.subscription.add(
      combineLatest([
        this.zoneName.valueChanges,
        this.selectedCategory.valueChanges,
        this.getMapPolygonData()
      ])
      .pipe(
        distinctUntilChanged()
      )
      .subscribe(([zoneName, selectedCategory, latLngs]) => {
        const polygonLines = (latLngs[0] as LatLng[])?.length;
        this.isSaveBtnDisabled = !(zoneName && selectedCategory && polygonLines >= MIN_POLYGON_LINES);
      })
    );
  }

  private getMapPolygonData(): Observable<LatLngArray> {
    return this.mapService.latLngs$.pipe(
      tap(latLngs => this.latLngs = latLngs)
    );
  }

  private loadCategories(): void {
    this.subscription.add(
      this.companyId$
        .pipe(
          switchMap((companyId) => this.getCategoriesList(companyId)),
          catchError((error) => {
            console.error('Error loading categories:', error);
            return of([]);
          })
        )
        .subscribe()
    );
  }

  private saveZone() {
    const requestBody = this.setSaveZoneReqBody();
    return this.coreZoneApiService.createZone(requestBody).pipe(
    ).subscribe((response: any) => {
      if (response.status === STATUSAPI.POST_SUCCESS) {
        this.reloadMapService.reinitializeMap();
        this.router.navigate([PAGE_URL.ZONE_EDITOR_URL]);
        this.snackbarService.showSuccessNotification(SNACKBAR_TEXT.SAVE_ZONE_SUCCESS_TEXT);
      }
    }, (error: HttpErrorResponse) => {
      this.snackbarService.showErrorNotification(error?.message || SNACKBAR_TEXT.SAVE_ZONE_ERROR_TEXT);
    });
  }

  private setSaveZoneReqBody(): IZonePostModel {
    // Reverse latlong
    // Add the last coordinate to match the first coordinate to ensure a closed shape.
    const coords = this.latLngs.map(polygon=> polygon.map(coord => [coord.lng, coord.lat]));
    coords[0].push(coords[0][0])
    const features = [{
        geometry: {
          type: "Polygon",
          coordinates: coords
        }
      }
    ];
    return {
      name: this.zoneName.value,
      categoryId: this.selectedCategory.value.id,
      geometry: { features } 
    }
  }

  private getCategoriesList(companyId: string): Observable<ICategory[]> {
    const param: IGetCategoryParams = { companyId };
    return this.coreZoneApiService.getCategories(param).pipe(
      tap((categories) => {
        this.categories = [...categories];
        if (this.categories.length > 0) {
          this.data$ = of(this.getCategoryData(this.categories));
        }
      }),
      catchError((error) => {
        console.error('Error fetching categories:', error);
        return throwError(error);
      })
    );
  }

  private setPageTitle(): void {
    this.pageTitle = this.router.url === PAGE_URL.CREATE_ZONE_URL ? 'Create zone' : 'Edit Zone';
  }

  onDeleteAccept() {
    /** Update with actual zoneId after GTCXFE-51: Edit zone is done. */
    const zoneId = '37747a13-e69c-49e8-96a5-c08f35771169';
    
    this.coreZoneApiService.deleteZone(zoneId)
      .subscribe(() => {
        this.reloadMapService.reinitializeMap();
        this.router.navigate([PAGE_URL.ZONE_EDITOR_URL]);
        this.snackbarService.showSuccessNotification(SNACKBAR_TEXT.DELETE_ZONE_SUCCESS_TEXT);
      }, (errorResp) => {
        this.snackbarService.showErrorNotification(errorResp?.error?.message || SNACKBAR_TEXT.DELETE_ZONE_ERROR_TEXT);
      });
  }

  onDelete() {
    this.openDeleteDialog();
  }

  openDeleteDialog() {
    this.dialog.open(DialogComponent, {
      data: {
        title: 'Delete zone',
        messages: [
          `By deleting a zone, it will no longer be displayed in the future and can’t be reported or alerted against.`,
          'Any historical mentions of the zone will continue to be associated with events.',
        ],
        cancelText: 'Cancel',
        acceptText: 'Delete',
        onAccept: () => this.onDeleteAccept(),
      },
      panelClass: 'delete-zone-confirmation-dialog',
      maxWidth: '360px',
    });
  }
}
